/** Route slug ↔ this module (`/legal/business-corporate-law`). */
export const BUSINESS_CORPORATE_LAW_SLUG = "business-corporate-law";

export const businessCorporateLawDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "3+", label: "Guides Available" },
    { value: "24h", label: "Expert Response" },
    { value: "5K+", label: "Ghanaians Helped" },
  ],
  funnelTagline:
    "Designed for real users (Ghanaians) + structured for InfoBoxx funnel (education → trust → conversion)",
  focusAreas: [
    "Business registration",
    "Company compliance",
    "Contracts and agreements",
    "Partnership disputes",
  ],
  primaryCtaHeading: "Talk to a business lawyer",
  introduction: {
    headline: "WHY BUSINESS & CORPORATE LAW MATTERS IN GHANA",
    lead: "Starting or running a business in Ghana is full of opportunity—but also legal responsibility.",
    manyPeopleLead: "What many people do:",
    manyPeople: [
      "Start businesses without registering properly",
      "Ignore compliance requirements",
      "Enter agreements without legal protection",
      "Face disputes with partners",
    ],
    resultLead: "Results are normally:",
    resultBullets: ["Business shutdowns", "Legal penalties", "Loss of money or ownership"],
    closing: "Business law protects your business, your money, and your future.",
  },
  businessRegistration: {
    title: "BUSINESS REGISTRATION IN GHANA (STEP-BY-STEP GUIDE)",
    whyHeading: "Why You Must Register Your Business",
    whyIntro: "Registering your business:",
    whyBullets: [
      "Makes it a legal entity",
      "Allows you to sign contracts and hire staff",
      "Helps you open a bank account",
      "Builds trust with customers",
    ],
    whereHeading: "Where to Register",
    whereLead: "All businesses must be registered with the:",
    orcLine: "Office of the Registrar of Companies (ORC)",
    structuresHeading: "Types of Business Structures in Ghana",
    structures: [
      "Sole Proprietorship",
      "Partnership",
      "Company Limited by Shares",
      "Company Limited by Guarantee",
      "External Company",
    ],
    structureImpactLead: "Choosing the right structure is critical—it affects:",
    structureImpact: ["taxes", "liability", "ownership"],
    processHeading: "Step-by-Step Registration Process",
    steps: [
      {
        number: 1,
        title: "Choose Business Structure",
        body: "Decide how your business will operate legally.",
      },
      {
        number: 2,
        title: "Choose and Check Business Name",
        bullets: ["Must be unique", "Must not already exist"],
      },
      {
        number: 3,
        title: "Submit Registration Forms",
        bullets: ["Provide company details", "Pay registration fees"],
      },
      {
        number: 4,
        title: "Receive Certificate",
        bullets: ["Usually within a few weeks"],
      },
    ],
    mistakesHeading: "Common Mistakes",
    mistakes: [
      "Choosing wrong business structure",
      "Using unregistered business name",
      "Not completing all legal steps",
    ],
  },
  compliance: {
    title: "COMPANY COMPLIANCE (VERY IMPORTANT)",
    opener: "Registering your business is just the beginning.",
    stayCompliantLead: "You must stay compliant.",
    whatMeansHeading: "What Compliance Means",
    whatMeans: "Following all legal and regulatory requirements after registration.",
    keyHeading: "Key Compliance Requirements in Ghana",
    requirements: [
      {
        title: "Tax Registration (GRA)",
        bullets: ["Get Tax Identification Number (TIN)", "File taxes regularly"],
      },
      {
        title: "SSNIT Registration",
        bullets: ["Required if you employ staff"],
      },
      {
        title: "Annual Returns Filing",
        bullets: ["Must be submitted yearly to ORC"],
      },
      {
        title: "Business Licenses & Permits",
        bullets: ["Depending on your industry"],
      },
    ],
    whyMattersHeading: "Why Compliance Matters",
    whyMatters: [
      "Avoid penalties or closure",
      "Maintain business credibility",
      "Stay legally protected",
    ],
    whyFooter: "Ghana is increasingly promoting a strong culture of compliance among businesses",
  },
  contracts: {
    title: "CONTRACTS & AGREEMENTS (CRITICAL FOR EVERY BUSINESS)",
    whatHeading: "What is a Contract?",
    whatBody: "A legally binding agreement between two or more parties.",
    commonHeading: "Common Business Contracts",
    common: [
      "Employment contracts",
      "Supplier agreements",
      "Partnership agreements",
      "Lease agreements",
      "Service contracts",
    ],
    elementsHeading: "Key Elements of a Valid Contract",
    elements: ["Offer", "Acceptance", "Consideration (value exchange)", "Intention to create legal relations"],
    problemsHeading: "Common Problems",
    problems: ["Verbal agreements (no proof)", "Unclear terms", "One-sided contracts", "No legal review"],
    bestPracticeHeading: "Best Practice",
    bestPracticeLead: "Always:",
    bestPractice: ["Put agreements in writing", "Use a lawyer to review"],
  },
  partnershipDisputes: {
    title: "PARTNERSHIP DISPUTES (VERY COMMON ISSUE)",
    intro:
      "Many businesses fail not because of poor ideas—but because of conflicts between partners.",
    causesHeading: "Common Causes of Disputes",
    causes: ["Profit sharing disagreements", "Lack of clear roles", "Misuse of funds", "Decision-making conflicts"],
    preventHeading: "How to Prevent Disputes",
    agreementLead: "Have a Partnership Agreement",
    agreementExplain: "This should define:",
    agreementBullets: ["Roles and responsibilities", "Profit sharing", "Exit terms"],
    recordsHeading: "Keep Records",
    records: ["Financial records", "Agreements", "Transactions"],
    ifDisputeHeading: "If a Dispute Happens",
    ifDisputeSteps: ["Try mediation", "Review agreement", "Seek legal advice", "Go to court if necessary"],
  },
  ownershipLiability: {
    title: "BUSINESS OWNERSHIP & LIABILITY (IMPORTANT CONCEPT)",
    separateHeading: "Separate Legal Entity",
    separateBody:
      "When you register a company:\n\nThe business becomes a separate legal person",
    mattersHeading: "Why This Matters",
    mattersBullets: ["Protects your personal assets", "Limits your liability"],
    riskHeading: "Risk",
    riskLead: "If not structured properly:",
    riskBullet: "You may be personally responsible for business debts",
  },
  intellectualProperty: {
    title: "INTELLECTUAL PROPERTY (PROTECT YOUR BUSINESS IDEA)",
    intro: "Many businesses ignore this.",
    protectHeading: "What You Can Protect",
    protect: ["Brand name (trademark)", "Logo", "Products", "Designs"],
    whyHeading: "Why It Matters",
    whyBullets: ["Prevents others from copying your business", "Protects your brand value"],
    lawFootnote:
      "Ghana has laws such as the Trademarks Act to protect businesses",
  },
  commonProblemsGhana: {
    title: "COMMON BUSINESS PROBLEMS IN GHANA",
    intro: "Top Issues",
    issues: [
      "Operating without registration",
      "Poor contract agreements",
      "Non-compliance with taxes",
      "Partnership conflicts",
      "Lack of legal advice",
    ],
  },
  practicalTips: {
    title: "PRACTICAL TIPS FOR GHANAIANS",
    beforeStart: {
      heading: "Before Starting a Business",
      items: ["Choose correct structure", "Register properly", "Understand your obligations"],
    },
    duringOps: {
      heading: "During Business Operations",
      items: ["Keep proper records", "File taxes", "Use written agreements"],
    },
    whenPartnering: {
      heading: "When Partnering",
      items: ["Define roles clearly", "Use a partnership agreement"],
    },
    whenExpanding: {
      heading: "When Expanding",
      items: ["Review legal structure", "Ensure compliance"],
    },
  },
  redFlags: {
    title: "RED FLAGS TO WATCH OUT FOR",
    items: [
      "Operating without registration",
      "Verbal business agreements",
      "Unclear ownership structure",
      "Ignoring tax obligations",
      "No written contracts",
      "Partner conflicts without agreements",
    ],
  },
  editorialPlaybook: {
    title: "INFOBOXX CONTENT IDEAS (HIGH CONVERSION)",
    articlesHeading: "Articles",
    articles: [
      '"How to Register a Business in Ghana (Step-by-Step)"',
      '"5 Mistakes That Can Collapse Your Business"',
      '"What Every Ghanaian Must Know Before Signing a Contract"',
    ],
    videosHeading: "Videos",
    videos: ["Business registration explained", "Contract mistakes to avoid", "Partnership tips"],
    leadCtasHeading: "CTAs",
    leadCtas: [
      "Register your business",
      "Talk to a business lawyer",
      "Review your contract",
      "Resolve a dispute",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hook: { label: "Hook", text: '"How do I register my business in Ghana?"' },
    education: { label: "Education", text: "Explain steps + compliance" },
    authority: { label: "Authority", text: "Legal framework (Act 992)" },
    conversion: { label: "Conversion", text: '"Speak to a business lawyer"' },
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    bullets: [
      "A business is not just an idea—it is a legal structure",
      "Contracts are not optional—they are protection tools",
      "Compliance is not a burden—it is business survival",
    ],
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "The difference between:",
    smooth: "a successful, scalable business",
    and: "and",
    stressful: "a failed or legally troubled business",
    closing:
      "…is legal structure, compliance, and proper agreements",
  },
  faqs: [
    {
      q: "Where do I register a business in Ghana?",
      a: "All businesses must be registered with the Office of the Registrar of Companies (ORC). Choosing the correct structure affects taxes, liability, and ownership.",
    },
    {
      q: "What does compliance include after registration?",
      a: "Typical obligations include Tax Identification Number (TIN) registration and filing at GRA, SSNIT if you employ staff, annual returns filing with ORC, and industry-specific licenses and permits.",
    },
    {
      q: "What makes a valid contract?",
      a: "A valid agreement usually requires offer, acceptance, consideration (value exchange), and intention to create legal relations. Putting agreements in writing and having them reviewed reduces disputes.",
    },
    {
      q: "How can I reduce partnership disputes?",
      a: "Use a written partnership agreement that defines roles, profit sharing, and exit terms; keep financial records and documentation; try mediation before litigation when conflicts arise.",
    },
    {
      q: "Why does separate legal entity status matter?",
      a: "A properly registered company can limit personal liability and protect personal assets from business debts—if the structure and operations are set up correctly.",
    },
  ],
  articles: [
    {
      id: "bcl-a-1",
      slug: "business-corporate-law-1",
      title: "How to Register a Business in Ghana (Step-by-Step)",
      excerpt:
        "Why registration matters, ORC and business structures, name checks, forms and fees, certificate timing, and common mistakes to avoid.",
      type: "Guide",
      readTime: "8 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: 'Read this InfoBoxx guide: "How to Register a Business in Ghana (Step-by-Step)"',
      coverImage:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
      tags: ["ORC", "Registration", "Ghana"],
      pullQuote:
        "Business law protects your business, your money, and your future.",
      takeaways: [
        "Choose correct structure",
        "Register properly",
        "Understand your obligations",
      ],
      video: {
        title: "Business registration explained",
        duration: "4:50",
        poster:
          "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Why You Must Register Your Business",
          text: "Registering your business:",
          bullets: [
            "Makes it a legal entity",
            "Allows you to sign contracts and hire staff",
            "Helps you open a bank account",
            "Builds trust with customers",
          ],
        },
        {
          heading: "Where to Register",
          text: "All businesses must be registered with the Office of the Registrar of Companies (ORC).",
        },
        {
          heading: "Types of Business Structures in Ghana",
          text: "",
          bullets: [
            "Sole Proprietorship",
            "Partnership",
            "Company Limited by Shares",
            "Company Limited by Guarantee",
            "External Company",
          ],
        },
        {
          heading: "Choosing the right structure",
          text: "Choosing the right structure is critical—it affects:",
          bullets: ["taxes", "liability", "ownership"],
        },
        {
          heading: "Step 1: Choose Business Structure",
          text: "Decide how your business will operate legally.",
        },
        {
          heading: "Step 2: Choose and Check Business Name",
          bullets: ["Must be unique", "Must not already exist"],
        },
        {
          heading: "Step 3: Submit Registration Forms",
          bullets: ["Provide company details", "Pay registration fees"],
        },
        {
          heading: "Step 4: Receive Certificate",
          bullets: ["Usually within a few weeks"],
        },
        {
          heading: "Common Mistakes",
          bullets: [
            "Choosing wrong business structure",
            "Using unregistered business name",
            "Not completing all legal steps",
          ],
        },
      ],
    },
    {
      id: "bcl-a-2",
      slug: "business-corporate-law-2",
      title: "5 Mistakes That Can Collapse Your Business",
      excerpt:
        "Operating without registration, weak contracts, tax non-compliance, partnership conflicts without agreements, and lack of legal advice—plus how compliance and structure help you survive.",
      type: "Checklist",
      readTime: "6 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: 'Read this InfoBoxx guide: "5 Mistakes That Can Collapse Your Business"',
      coverImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
      tags: ["Compliance", "Risk", "Operations"],
      pullQuote:
        "The difference between a successful, scalable business and a failed or legally troubled business is legal structure, compliance, and proper agreements",
      takeaways: [
        "Keep proper records",
        "File taxes",
        "Use written agreements",
      ],
      video: {
        title: "Contract mistakes to avoid",
        duration: "4:12",
        poster:
          "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "COMMON BUSINESS PROBLEMS IN GHANA",
          text: "Top Issues",
          bullets: [
            "Operating without registration",
            "Poor contract agreements",
            "Non-compliance with taxes",
            "Partnership conflicts",
            "Lack of legal advice",
          ],
        },
        {
          heading: "Why Compliance Matters",
          bullets: [
            "Avoid penalties or closure",
            "Maintain business credibility",
            "Stay legally protected",
          ],
        },
        {
          heading: "Common Problems (Contracts)",
          bullets: [
            "Verbal agreements (no proof)",
            "Unclear terms",
            "One-sided contracts",
            "No legal review",
          ],
        },
        {
          heading: "Risk (Ownership & Liability)",
          text: "If not structured properly:",
          bullets: ["You may be personally responsible for business debts"],
        },
        {
          heading: "Red Flags to Watch Out For",
          bullets: [
            "Operating without registration",
            "Verbal business agreements",
            "Unclear ownership structure",
            "Ignoring tax obligations",
            "No written contracts",
            "Partner conflicts without agreements",
          ],
        },
      ],
    },
    {
      id: "bcl-a-3",
      slug: "business-corporate-law-3",
      title: "What Every Ghanaian Must Know Before Signing a Contract",
      excerpt:
        "What a contract is, common business contracts, validity elements (offer, acceptance, consideration, intention), pitfalls, and why written agreements reviewed by counsel matter.",
      type: "Expert Explainer",
      readTime: "7 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText:
        'Read this InfoBoxx guide: "What Every Ghanaian Must Know Before Signing a Contract"',
      coverImage:
        "https://images.unsplash.com/photo-1563986768494-de4b277cdd52?auto=format&fit=crop&w=800&q=80",
      tags: ["Contracts", "Negotiation", "Protection"],
      pullQuote:
        "Contracts are not optional—they are protection tools",
      takeaways: [
        "Put agreements in writing",
        "Use a lawyer to review",
        "Define roles when partnering",
      ],
      video: {
        title: "Partnership tips",
        duration: "3:55",
        poster:
          "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "What is a Contract?",
          text: "A legally binding agreement between two or more parties.",
        },
        {
          heading: "Common Business Contracts",
          bullets: [
            "Employment contracts",
            "Supplier agreements",
            "Partnership agreements",
            "Lease agreements",
            "Service contracts",
          ],
        },
        {
          heading: "Key Elements of a Valid Contract",
          bullets: [
            "Offer",
            "Acceptance",
            "Consideration (value exchange)",
            "Intention to create legal relations",
          ],
        },
        {
          heading: "Common Problems",
          bullets: [
            "Verbal agreements (no proof)",
            "Unclear terms",
            "One-sided contracts",
            "No legal review",
          ],
        },
        {
          heading: "Best Practice",
          text: "Always:",
          bullets: ["Put agreements in writing", "Use a lawyer to review"],
        },
        {
          heading: "If a Dispute Happens",
          text: "",
          bullets: [
            "Try mediation",
            "Review agreement",
            "Seek legal advice",
            "Go to court if necessary",
          ],
        },
      ],
    },
  ],
  videos: [
    {
      id: "bcl-v-1",
      title: "Business registration explained",
      duration: "4:50",
      poster:
        "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      description: "ORC steps, structures, and why the right entity choice matters for tax and liability.",
    },
    {
      id: "bcl-v-2",
      title: "Contract mistakes to avoid",
      duration: "4:12",
      poster:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      description: "Written terms, review, and red flags that trigger avoidable litigation.",
    },
    {
      id: "bcl-v-3",
      title: "Partnership tips",
      duration: "3:55",
      poster:
        "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
      description: "Agreements, records, and resolution paths when co-founders disagree.",
    },
  ],
  podcasts: [
    {
      id: "bcl-p-1",
      episodeLabel: "Episode 1",
      duration: "27 min",
      title: "Act 992 & ORC: Registration Without the Jargon",
      description: "Entities, certificates, and what founders should prepare before filing.",
      host: "Afrimore Legal Desk",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "bcl-p-2",
      episodeLabel: "Episode 2",
      duration: "31 min",
      title: "GRA, SSNIT & Annual Returns — Compliance That Sticks",
      description: "Rhythms for tax filing, employer duties, and ORC annual returns.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "April 2026",
      coverImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "bcl-p-3",
      episodeLabel: "Mini",
      duration: "18 min",
      title: "Trademarks & Brand Protection in Ghana",
      description: "Why registration and the Trademarks Act matter for growing consumer brands.",
      host: "Afrimore Legal Desk",
      publishedAt: "March 2026",
      coverImage:
        "https://images.unsplash.com/photo-1563986768494-de4b277cdd52?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
